import { type AfterViewInit, Component, computed, type ElementRef, inject, signal, viewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule, type MatSelectionListChange } from '@angular/material/list';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DtisFormDialogComponent } from '@dtis/angular';
import { TranslocoModule } from '@jsverse/transloco';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import type { AzureUser } from '../../../../models';
import { ManagementService } from '../../../../services/management.service';

interface UserWithRoles {
  name: string;
  roles: string[];
}

@Component({
  selector: 'dtis-admin-user-search-dialog',
  imports: [
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatIconModule,
    MatListModule,
    MatButtonModule,
    MatChipsModule,
    MatProgressBarModule,
    MatTooltipModule,
    TranslocoModule,
    DtisFormDialogComponent,
  ],
  template: `
    <dtis-form-dialog
      [title]="dialogTitle()"
      (saved)="onSave()"
      (cancelled)="onCancel()"
    >
      <div class="flex flex-col gap-4 mt-5">
        <mat-form-field class="w-full" appearance="outline">
          <mat-label>{{ 'user-search' | transloco }}</mat-label>
          <input
            #searchInput
            matInput
            placeholder="Search users by surname or given name"
            [formControl]="searchControl"
            autocomplete="off"
          />
          @if (searchControl.value) {
            <button
              mat-icon-button
              matSuffix
              type="button"
              aria-label="Clear search"
              (click)="searchControl.setValue('')"
            >
              <mat-icon>close</mat-icon>
            </button>
          }
        </mat-form-field>
        @if (error(); as err) {
          <div
            class="px-4 py-3 bg-error/10 text-error rounded border border-error/30 text-sm"
          >
            {{ err }}
          </div>
        }
        @if (pinnedUsers().length > 0) {
          <section aria-live="polite">
            <div class="flex items-center justify-between mb-1">
              <span class="text-sm font-medium">
                {{ 'user-search-selected' | transloco }} ({{
                  pinnedUsers().length
                }})
              </span>
              <button mat-button type="button" (click)="clearPinned()">
                {{ 'user-search-clear-all' | transloco }}
              </button>
            </div>
            <mat-chip-set
              [attr.aria-label]="'user-search-selected' | transloco"
            >
              @for (user of pinnedUsers(); track user.userPrincipalName) {
                <mat-chip-row
                  [matTooltip]="user.mail"
                  (removed)="unpin(user.userPrincipalName)"
                >
                  {{ user.displayName }}
                  <button
                    matChipRemove
                    [attr.aria-label]="
                      ('user-search-remove' | transloco) +
                      ' ' +
                      user.displayName
                    "
                  >
                    <mat-icon>cancel</mat-icon>
                  </button>
                </mat-chip-row>
              }
            </mat-chip-set>
          </section>
        }
        @if (loading()) {
          <mat-progress-bar mode="indeterminate" />
        }
        <mat-selection-list
          [multiple]="true"
          (selectionChange)="onSelectionChange($event)"
        >
          @for (user of azureUsers(); track user.identifier) {
            <mat-list-option
              [value]="user.userPrincipalName"
              [selected]="isPinned(user.userPrincipalName)"
            >
              <span matListItemTitle>{{ user.displayName }}</span>
              <span matListItemLine>{{ user.mail }}</span>
            </mat-list-option>
          }
        </mat-selection-list>
        @if (!loading() && !error()) {
          @if (!searchTerm() || searchTerm().length < 2) {
            <p class="text-sm text-gray-500 text-center">
              {{ 'user-search-hint' | transloco }}
            </p>
          } @else if (azureUsers().length === 0) {
            <p class="text-sm text-gray-500 text-center">
              {{ 'user-search-no-results' | transloco }}
            </p>
          }
        }
      </div>
    </dtis-form-dialog>
  `,
})
/**
 * Dialog component for searching and selecting users.
 *
 * - Allows searching users from Azure AD with debounced search.
 * - Users can be selected and added to a chip list.
 * - Selected users are returned to the parent component on save.
 */
export class UserSearchDialogComponent implements AfterViewInit {
  private readonly managementService = inject(ManagementService);
  searchInputRef = viewChild<ElementRef>('searchInput');
  /**
   * Reference to the dialog, used for closing and returning data.
   */
  public readonly dialogRef = inject(MatDialogRef<UserSearchDialogComponent, string[]>);
  /**
   * Data injected into the dialog, containing the initially selected users.
   */
  public data: { selectedUsers: UserWithRoles[]; role?: string } = inject(MAT_DIALOG_DATA);

  /**
   * Dialog title signal based on role
   */
  dialogTitle = signal('add-members');

  /**
   * Form control for the user search input.
   */
  searchControl = new FormControl('');
  /**
   * Signal containing the list of Azure users from the search.
   */
  azureUsers = signal<AzureUser[]>([]);
  /**
   * Signal containing error message from user search.
   */
  error = signal<string | null>(null);
  loading = signal(false);
  searchTerm = signal('');
  /**
   * Users picked across all searches, kept until the dialog is saved.
   */
  private readonly pinned = signal<Map<string, AzureUser>>(new Map());
  pinnedUsers = computed(() => [...this.pinned().values()]);

  /**
   * List of currently selected user identifiers.
   */
  selectedUsers: UserWithRoles[];

  /**
   * Initializes the dialog with the provided selected users and sets up debounced search.
   */
  constructor() {
    this.selectedUsers = [...(this.data?.selectedUsers ?? [])];

    // Set dialog title based on role
    if (this.data?.role === 'Administrator') {
      this.dialogTitle.set('add-administrator');
    } else if (this.data?.role === 'User') {
      this.dialogTitle.set('add-user');
    }

    this.searchControl.valueChanges.subscribe((v) => this.searchTerm.set((v ?? '').trim()));

    // Set up debounced search
    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter((value) => value !== null && value.trim().length >= 2)
      )
      .subscribe((searchTerm) => {
        // Clear error before new search
        this.error.set(null);
        if (!searchTerm) {
          this.azureUsers.set([]);
          return;
        }
        // Normalize search: replace commas with spaces, trim parts
        const normalized = searchTerm.replaceAll(',', ' ').trim();
        this.loading.set(true);
        this.managementService.getAzureUser(normalized).subscribe({
          next: (response) => {
            this.loading.set(false);
            this.azureUsers.set(response?.azureUsers ?? []);
            this.error.set(null);
          },
          error: (error) => {
            this.loading.set(false);
            console.error('Error fetching Azure users:', error);
            this.azureUsers.set([]);
            this.error.set('Failed to search users. Please try again.');
          },
        });
      });
  }

  ngAfterViewInit(): void {
    // Auto-focus the search input when dialog opens
    setTimeout(() => {
      this.searchInputRef()?.nativeElement?.focus();
    });
  }

  /**
   * Checks if a user is currently selected.
   * @param userId The user identifier to check
   * @returns True if the user is selected, false otherwise
   */
  isSelected(userId: string): boolean {
    return this.selectedUsers.some((user) => user.name === userId);
  }

  isPinned(upn: string): boolean {
    return this.pinned().has(upn);
  }

  onSelectionChange(event: MatSelectionListChange): void {
    const next = new Map(this.pinned());
    for (const option of event.options) {
      const upn = option.value as string;
      const user = this.azureUsers().find((u) => u.userPrincipalName === upn);
      if (option.selected && user) {
        next.set(upn, user);
      } else if (!option.selected) {
        next.delete(upn);
      }
    }
    this.pinned.set(next);
  }

  clearPinned(): void {
    this.pinned.set(new Map());
  }

  unpin(upn: string): void {
    const next = new Map(this.pinned());
    next.delete(upn);
    this.pinned.set(next);
  }

  /**
   * Closes the dialog and returns all pinned users.
   */
  onSave() {
    this.dialogRef.close([...this.pinned().keys()]);
  }

  /**
   * Closes the dialog without returning any data.
   */
  onCancel() {
    this.dialogRef.close();
  }
}
